/**
 * 承認モーダルコンポーネント
 * ビルドの承認/却下を行うモーダル
 */

import React, { useState } from 'react';
import { Modal, Button } from './ui';
import { Icon } from './icons';

export default function ApprovalModal({
  isOpen,
  onClose,
  build,
  onApprove,
  onReject,
  loading = false,
}) {
  const [comment, setComment] = useState('');
  const [releaseVersion, setReleaseVersion] = useState('');
  const [action, setAction] = useState(null); // 'approve' or 'reject'

  const handleClose = () => {
    setComment('');
    setReleaseVersion('');
    setAction(null);
    onClose();
  };

  const handleApprove = () => {
    const trimmedVersion = releaseVersion.trim();
    if (trimmedVersion && !/^\d+\.\d+\.\d+(-\w+)?$/.test(trimmedVersion)) {
      alert('リリースバージョンは X.Y.Z または X.Y.Z-suffix 形式で入力してください');
      return;
    }
    setAction('approve');
    onApprove(comment, trimmedVersion || undefined);
  };

  const handleReject = () => {
    if (!comment.trim()) {
      alert('却下理由を入力してください');
      return;
    }
    setAction('reject');
    onReject(comment);
  };

  if (!build) return null;

  const buildId = build.buildId || build.id;
  const sitlStatus = build.testResults?.sitl?.status;
  const canApprove = sitlStatus === 'PASSED' || sitlStatus === 'SKIPPED';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="ビルド承認"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={loading}>
            キャンセル
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={loading || action === 'approve'}
          >
            {loading && action === 'reject' ? (
              <>
                <Icon name="loader" className="w-4 h-4 animate-spin mr-2" />
                処理中...
              </>
            ) : (
              <>
                <Icon name="x" className="w-4 h-4 mr-2" />
                却下
              </>
            )}
          </Button>
          <Button
            variant="primary"
            onClick={handleApprove}
            disabled={loading || !canApprove || action === 'reject'}
          >
            {loading && action === 'approve' ? (
              <>
                <Icon name="loader" className="w-4 h-4 animate-spin mr-2" />
                承認中...
              </>
            ) : (
              <>
                <Icon name="check" className="w-4 h-4 mr-2" />
                承認
              </>
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* ビルド情報 */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-500 mb-3">ビルド情報</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400">ビルドID</p>
              <p className="font-mono text-sm">{buildId}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">リポジトリ</p>
              <p className="text-sm">{build.repository}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">ブランチ</p>
              <p className="text-sm">{build.branch}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">コミット</p>
              <p className="font-mono text-sm">{build.commitSha?.slice(0, 8) || '-'}</p>
            </div>
          </div>
          {build.commitMessage && (
            <div className="mt-3">
              <p className="text-xs text-slate-400">コミットメッセージ</p>
              <p className="text-sm text-slate-700">{build.commitMessage}</p>
            </div>
          )}
        </div>

        {/* テスト結果サマリー */}
        <div>
          <h4 className="text-sm font-medium text-slate-500 mb-3">テスト結果</h4>
          <div className="grid grid-cols-2 gap-4">
            {/* SiTL */}
            <div className={`rounded-lg p-4 ${
              sitlStatus === 'PASSED' ? 'bg-green-50 border border-green-200' :
              sitlStatus === 'SKIPPED' ? 'bg-slate-100 border border-slate-300' :
              sitlStatus === 'FAILED' ? 'bg-red-50 border border-red-200' :
              sitlStatus === 'RUNNING' ? 'bg-blue-50 border border-blue-200' :
              'bg-slate-50 border border-slate-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">SiTLテスト</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  sitlStatus === 'PASSED' ? 'bg-green-100 text-green-700' :
                  sitlStatus === 'SKIPPED' ? 'bg-slate-200 text-slate-600' :
                  sitlStatus === 'FAILED' ? 'bg-red-100 text-red-700' :
                  sitlStatus === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {sitlStatus === 'PASSED' ? '合格' :
                   sitlStatus === 'SKIPPED' ? 'スキップ' :
                   sitlStatus === 'FAILED' ? '不合格' :
                   sitlStatus === 'RUNNING' ? '実行中' : '未実行'}
                </span>
              </div>
              {build.testResults?.sitl && (
                <p className="text-xs text-slate-600">
                  {build.testResults.sitl.passed || 0}/{build.testResults.sitl.totalTests || 0} 合格
                </p>
              )}
            </div>

            {/* HiLS */}
            <div className="rounded-lg p-4 bg-slate-50 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">HiLSテスト</span>
                <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-500">
                  準備中
                </span>
              </div>
              <p className="text-xs text-slate-400">
                将来実装予定
              </p>
            </div>
          </div>
        </div>

        {/* 承認条件 */}
        {!canApprove && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="alert-triangle" className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">承認できません</p>
                <p className="text-xs text-amber-600 mt-1">
                  SiTLテストが合格していないビルドは承認できません。テストを実行し、全項目が合格してから承認してください。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* リリースバージョン入力（承認時のみ使用） */}
        {canApprove && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              リリースバージョン <span className="text-slate-400 text-xs">（任意）</span>
            </label>
            <input
              type="text"
              value={releaseVersion}
              onChange={(e) => setReleaseVersion(e.target.value)}
              placeholder="例: 1.2.0（未入力の場合は 0.0.0-{gitハッシュ} で自動採番）"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            />
            <p className="text-xs text-slate-400 mt-1">
              承認時にビルド成果物（RAUCバンドル）がこのバージョンでファームウェア登録されます。
            </p>
          </div>
        )}

        {/* コメント入力 */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            コメント {!canApprove && <span className="text-red-500">（却下時は必須）</span>}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="承認・却下に関するコメントを入力..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={3}
          />
        </div>

        {/* 承認後の処理説明 */}
        {canApprove && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="info" className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">承認後の処理</p>
                <p className="text-xs text-blue-600 mt-1">
                  承認すると、ビルド成果物（RAUCバンドル .raucb）が本番S3バケットへコピーされ、
                  ファームウェアとして released 状態で登録されます。
                  登録後はデバイス詳細画面・OTA更新画面から配信対象として選択できます。
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
